import "../../pages/businessAnalysis/businessAnalysis.css";
import "./compliance.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Dropdown, Input, Segmented, Table, Tabs, Tag, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getDocumentRecipientsQuery, getDocumentVersionsQuery, getSignedCopyUrlQuery } from "@src/util/query/compliance.query";
import { exportComplianceAuditMutation, finalizeDocumentUploadMutation, newDocumentVersionMutation, sendComplianceRemindersMutation, syncDocumentRecipientsMutation } from "@src/util/mutation/compliance.mutation";
import { describeAssignment, formatDate, formatDateTime, METHOD_LABEL, REVOKED_REASON, runAuditExport, STATUS_META, uploadThenFinalize } from "./complianceHelpers";

/**
 * Who a compliance document went to and where each officer is up to, plus its version
 * history. Opened from a record's Documents tab and from the Compliance page. With
 * `allowNewVersion` (documents, not toolbox talks) it can upload the next version itself; an
 * archived document shows who archived it and, with `onRestore(id)`, offers to restore it.
 */
export const RecipientsDrawer = ({ visible, documentId, onClose, onChanged = () => {}, allowNewVersion = false, onRestore }) => {
    const dispatch = useDispatch();
    // the current version - moves on when a new version is uploaded from here
    const [currentDocumentId, setCurrentDocumentId] = useState(documentId);
    const [activeDocumentId, setActiveDocumentId] = useState(documentId);
    const [tab, setTab] = useState("recipients");
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    useEffect(() => {
        setCurrentDocumentId(documentId);
        setActiveDocumentId(documentId);
        setTab("recipients");
        setFilter("all");
        setSelectedRowKeys([]);
    }, [documentId]);

    const { data, loading, refetch } = useQuery(getDocumentRecipientsQuery, {
        variables: { documentId: activeDocumentId },
        skip: !visible || !activeDocumentId,
        fetchPolicy: "network-only",
    });
    const { data: versionData, loading: versionsLoading } = useQuery(getDocumentVersionsQuery, {
        variables: { documentId: currentDocumentId },
        skip: !visible || !currentDocumentId,
        fetchPolicy: "network-only",
    });

    const [getSignedCopy] = useLazyQuery(getSignedCopyUrlQuery, { fetchPolicy: "network-only" });
    const [sendReminders, { loading: reminding }] = useMutation(sendComplianceRemindersMutation);
    const [syncRecipients, { loading: syncing }] = useMutation(syncDocumentRecipientsMutation);
    const [exportAudit, { loading: exporting }] = useMutation(exportComplianceAuditMutation);
    const [newDocumentVersion] = useMutation(newDocumentVersionMutation);
    const [finalizeUpload] = useMutation(finalizeDocumentUploadMutation);
    const versionInputRef = useRef(null);
    const [uploadingVersion, setUploadingVersion] = useState(false);

    const detail = data?.getDocumentRecipients?.response;
    const versions = versionData?.getDocumentVersions?.response || [];
    const recipients = detail?.recipients || [];
    const isCurrentVersion = !detail?.isArchived;

    const counts = useMemo(() => ({
        all: recipients.filter((r) => r.status !== "revoked").length,
        outstanding: recipients.filter((r) => (r.status === "pending" || r.status === "overdue") && !r.leaver).length,
        overdue: recipients.filter((r) => r.status === "overdue" && !r.leaver).length,
        signed: recipients.filter((r) => r.status === "acknowledged").length,
        withdrawn: recipients.filter((r) => r.status === "revoked").length,
    }), [recipients]);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return recipients
            .filter((r) => {
                if (filter === "all") return r.status !== "revoked";
                if (filter === "outstanding") return (r.status === "pending" || r.status === "overdue") && !r.leaver;
                if (filter === "overdue") return r.status === "overdue" && !r.leaver;
                if (filter === "signed") return r.status === "acknowledged";
                if (filter === "withdrawn") return r.status === "revoked";
                return true;
            })
            .filter((r) => !needle || `${r.name} ${r.employeeNo} ${r.email}`.toLowerCase().includes(needle))
            .map((r) => ({ ...r, key: r._id }));
    }, [recipients, filter, search]);

    const outstandingIds = recipients.filter((r) => (r.status === "pending" || r.status === "overdue") && !r.leaver).map((r) => r._id);

    const notify = (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));

    const remind = async (shareIds) => {
        try {
            const res = await sendReminders({ variables: { shareIds } });
            notify(res?.data?.sendComplianceReminders?.message || "Reminders sent");
            setSelectedRowKeys([]);
            await refetch();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const sync = async () => {
        try {
            const res = await syncRecipients({ variables: { documentId: activeDocumentId } });
            const result = res?.data?.syncDocumentRecipients?.response;
            const changed = (result?.assigned || 0) + (result?.reopened || 0) + (result?.revoked || 0);
            notify(changed ? describeAssignment(result) : "Recipients are already up to date");
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
        }
    };

    // "Upload new version": the file replaces this version, everyone it applies to is asked to
    // sign again, and the drawer moves on to the new version
    const onVersionFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        try {
            setUploadingVersion(true);
            const res = await newDocumentVersion({ variables: { documentId: currentDocumentId, input: { fileName: file.name } } });
            const { url, documentId: newId, version } = res?.data?.newDocumentVersion?.response || {};
            const result = await uploadThenFinalize({ url, documentId: newId, file, finalize: finalizeUpload });
            setCurrentDocumentId(newId);
            setActiveDocumentId(newId);
            setTab("recipients");
            setFilter("all");
            setSelectedRowKeys([]);
            onChanged();
            notify(`v${version} of ${detail?.title || "the document"} uploaded - ${describeAssignment(result).toLowerCase()} to sign again${result?.fieldsCarriedOver === false ? ". Its pages are different, so place the fields on it again." : ""}`);
        } catch (err) {
            notify(err.message, true);
        } finally {
            setUploadingVersion(false);
        }
    };

    const openSignedCopy = async (shareId) => {
        try {
            const res = await getSignedCopy({ variables: { shareId } });
            if (res?.error) throw res.error;
            const url = res?.data?.getSignedCopyUrl?.response?.url;
            if (url) window.open(url, "_blank");
        } catch (err) {
            notify(err.message, true);
        }
    };

    const columns = [
        {
            title: "Officer",
            dataIndex: "name",
            render: (_, r) => (
                <div className="cmp-person">
                    <span className="cmp-person-name">{r.name}</span>
                    <span className="cmp-muted">
                        {r.employeeNo || r.email || ""}
                        {r.leaver ? <Tag style={{ marginLeft: 6 }}>Leaver</Tag> : null}
                    </span>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (status, r) => {
                const meta = STATUS_META[status] || { label: status, color: "default" };
                const tag = <Tag color={meta.color}>{meta.label}</Tag>;
                return status === "revoked" && r.revokedReason
                    ? <Tooltip title={`Withdrawn - ${REVOKED_REASON[r.revokedReason] || r.revokedReason}`}>{tag}</Tooltip>
                    : tag;
            },
        },
        { title: "Assigned", dataIndex: "assignedAt", render: (v) => formatDate(v) },
        {
            title: "Due",
            dataIndex: "dueAt",
            render: (v, r) => <span className={r.status === "overdue" ? "cmp-overdue" : ""}>{formatDate(v)}</span>,
        },
        { title: "First opened", dataIndex: "firstViewedAt", render: (v) => formatDateTime(v) },
        {
            title: "Signed",
            dataIndex: "acknowledgedAt",
            render: (v, r) => r.status === "acknowledged"
                ? (
                    <div className="cmp-person">
                        <span>{formatDateTime(v)}</span>
                        <span className="cmp-muted">{METHOD_LABEL[r.acknowledgementMethod] || ""}{r.typedName ? ` · "${r.typedName}"` : ""}</span>
                    </div>
                )
                : "--",
        },
        {
            title: "Reminders",
            dataIndex: "remindersSent",
            render: (v, r) => v > 0 ? <Tooltip title={`Last sent ${formatDateTime(r.lastRemindedAt)}`}>{v}</Tooltip> : "--",
        },
        {
            title: "",
            dataIndex: "actions",
            align: "right",
            render: (_, r) => (
                r.hasSignedCopy
                    ? <button className="sm-btn cmp-nowrap" onClick={() => openSignedCopy(r._id)}>Signed copy</button>
                    : (r.status === "pending" || r.status === "overdue") && !r.leaver && isCurrentVersion
                        ? <button className="sm-btn cmp-nowrap" disabled={reminding} onClick={() => remind([r._id])}>Remind</button>
                        : null
            ),
        },
    ];

    const versionColumns = [
        {
            title: "Version",
            dataIndex: "version",
            render: (v, r) => (
                <span>
                    v{v} {r.current ? <Tag color="green" style={{ marginLeft: 6 }}>Current</Tag> : (
                        <Tooltip title={r.archivedReason === "superseded" ? null : `Archived ${formatDate(r.archivedAt)}${r.archivedBy ? ` by ${r.archivedBy}` : ""}`}>
                            <Tag style={{ marginLeft: 6 }}>{r.archivedReason === "superseded" ? "Replaced" : "Archived"}</Tag>
                        </Tooltip>
                    )}
                </span>
            ),
        },
        {
            title: "Uploaded",
            dataIndex: "uploadedAt",
            render: (v, r) => (
                <div className="cmp-person">
                    <span>{formatDateTime(v)}</span>
                    <span className="cmp-muted">{r.uploadedBy ? `by ${r.uploadedBy}` : ""}</span>
                    {r.restoreReason && <span className="cmp-muted">Restored: {r.restoreReason}</span>}
                </div>
            ),
        },
        {
            title: "File",
            dataIndex: "fileName",
            render: (v, r) => <button className="cmp-link" onClick={() => window.open(window.location.origin + "/user/preview?object=" + encodeURIComponent(r.fileKey), "_blank")}>{v}</button>,
        },
        {
            title: "Fingerprint",
            dataIndex: "fileHash",
            render: (v) => v ? <Tooltip title={`SHA-256 ${v}`}><span className="cmp-mono">{v.slice(0, 12)}…</span></Tooltip> : "--",
        },
        { title: "Signed", dataIndex: "acknowledged" },
        { title: "Outstanding", dataIndex: "outstanding" },
        { title: "Withdrawn", dataIndex: "withdrawn" },
        {
            title: "",
            dataIndex: "open",
            align: "right",
            render: (_, r) => <button className="sm-btn cmp-nowrap" onClick={() => { setActiveDocumentId(r._id); setTab("recipients"); setFilter(r.current ? "all" : "signed"); }}>Recipients</button>,
        },
    ];

    return (
        <Drawer
            title={detail ? `${detail.title} · v${detail.version}` : "Recipients"}
            placement="right"
            open={visible}
            onClose={onClose}
            width={1000}
            closable
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" style={{marginRight:'30px'}}/>}
            destroyOnClose
            footer={
                <div className="drawer-footer" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                        {isCurrentVersion && (
                            <button className="drawer-filled-btn" disabled={reminding || outstandingIds.length === 0} onClick={() => remind(selectedRowKeys.length ? selectedRowKeys : outstandingIds)}>
                                {selectedRowKeys.length ? `Remind selected (${selectedRowKeys.length})` : `Remind all outstanding (${outstandingIds.length})`}
                            </button>
                        )}
                        {isCurrentVersion && (
                            <Tooltip title="Re-check who this document applies to now (e.g. after officers were moved between sites outside WFC360) and send it to anyone missing.">
                                <button className="drawer-outlined-btn" disabled={syncing} onClick={sync}>Refresh recipients</button>
                            </Tooltip>
                        )}
                    </div>
                    <button className="drawer-outlined-btn" onClick={onClose}>Close</button>
                </div>
            }
        >
            {!isCurrentVersion && detail && detail.archive?.reason !== "retired" && (
                <div className="ba-callout info">
                    <div>
                        <div className="ba-callout-title">You're viewing v{detail.version}, which has been replaced.</div>
                        Signatures on this version stay on record. Outstanding requests were withdrawn when it was replaced.
                        {activeDocumentId !== currentDocumentId && <>{" "}<button className="ba-link-btn" onClick={() => { setActiveDocumentId(currentDocumentId); setFilter("all"); }}>Back to the current version</button></>}
                    </div>
                </div>
            )}
            {detail?.archive?.reason === "retired" && (
                <div className="ba-callout warning" data-archived-callout>
                    <div>
                        <div className="ba-callout-title">Archived {formatDate(detail.archive.at)}{detail.archive.by ? ` by ${detail.archive.by}` : ""}</div>
                        {detail.kind === "toolboxTalk" ? "Acknowledgements" : "Signatures"} given before it was archived stay on record; requests still outstanding were withdrawn.
                        {detail.archive.restore?.mode === "no" && <> {detail.archive.restore.reason}</>}
                        {onRestore && detail.archive.restore && detail.archive.restore.mode !== "no" && (
                            <>{" "}<button className="ba-link-btn" onClick={() => onRestore(detail.documentId)} data-drawer-restore>
                                {detail.archive.restore.mode === "asIs" ? "Restore it" : detail.kind === "toolboxTalk" ? "Restore it as a new issue" : "Restore it with a new version"}
                            </button></>
                        )}
                    </div>
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <div>
                {detail?.uploadedAt && (
                    <div className="cmp-muted" style={{ fontSize: 12, marginBottom: 10 }} data-uploaded-by>
                        Uploaded {formatDateTime(detail.uploadedAt)}{detail.uploadedBy ? ` by ${detail.uploadedBy}` : ""}
                    </div>
                )}
                <div className="ba-stat-strip">
                    <div className="ba-stat"><span className="ba-stat-num">{counts.signed}</span><span className="ba-stat-lbl">Signed</span></div>
                    <div className="ba-stat"><span className="ba-stat-num">{counts.outstanding}</span><span className="ba-stat-lbl">Outstanding</span></div>
                    <div className="ba-stat"><span className={`ba-stat-num${counts.overdue ? " cmp-overdue" : ""}`}>{counts.overdue}</span><span className="ba-stat-lbl">Overdue</span></div>
                    <div className="ba-stat"><span className="ba-stat-num">{counts.all ? Math.round((counts.signed / Math.max(1, counts.signed + counts.outstanding)) * 100) : 0}%</span><span className="ba-stat-lbl">Complete</span></div>
                </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <Dropdown
                        trigger={["click"]}
                        menu={{
                            items: [
                                { key: "pdf", label: "Signature register (PDF)" },
                                { key: "csv", label: "Spreadsheet (CSV)" },
                            ],
                            onClick: ({ key }) => runAuditExport({ exportAudit, input: { documentId: activeDocumentId, format: key }, notify }),
                        }}
                    >
                        <button className="drawer-outlined-btn" disabled={exporting} data-export>{exporting ? "Preparing..." : "Export ▾"}</button>
                    </Dropdown>
                    {isCurrentVersion && allowNewVersion && detail?.kind !== "toolboxTalk" && (
                        <Tooltip title="Replace the file. Signatures on this version stay on record; everyone it applies to is asked to sign the new one.">
                            <button className="drawer-filled-btn" disabled={uploadingVersion} onClick={() => versionInputRef.current?.click()} data-upload-version>
                                {uploadingVersion ? "Uploading..." : "Upload new version"}
                            </button>
                        </Tooltip>
                    )}
                </div>
            </div>

            <div className="setting-body-inner grid-tabs" style={{paddingLeft:'0'}}>
                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        { key: "recipients", label: `Recipients (${counts.all})` },
                        { key: "versions", label: `Versions (${versions.length || 1})` },
                    ]}
                />
            </div>

            {tab === "recipients" ? (
                <>
                    <div className="cmp-toolbar">
                        <Segmented
                            value={filter}
                            onChange={(v) => { setFilter(v); setSelectedRowKeys([]); }}
                            options={[
                                { label: `All (${counts.all})`, value: "all" },
                                { label: `Outstanding (${counts.outstanding})`, value: "outstanding" },
                                { label: `Overdue (${counts.overdue})`, value: "overdue" },
                                { label: `Signed (${counts.signed})`, value: "signed" },
                                { label: `Withdrawn (${counts.withdrawn})`, value: "withdrawn" },
                            ]}
                        />
                        <Input
                            placeholder="Search officer"
                            className="generic-input-control"
                            style={{ width: 240 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            suffix={<FontAwesomeIcon icon={faSearch} style={{ color: "#0091ae" }} />}
                        />
                    </div>
                    <Table
                        className="curvedTable cmp-drawer-table"
                        loading={loading || uploadingVersion}
                        columns={columns}
                        dataSource={rows}
                        pagination={{ pageSize: 50, hideOnSinglePage: true }}
                        locale={{ emptyText: filter === "all" ? "No one has been sent this document yet - check the site has officers allocated." : "No one here" }}
                        rowSelection={isCurrentVersion ? {
                            selectedRowKeys,
                            onChange: setSelectedRowKeys,
                            getCheckboxProps: (r) => ({ disabled: !((r.status === "pending" || r.status === "overdue") && !r.leaver) }),
                        } : undefined}
                    />
                </>
            ) : (
                <Table
                    className="curvedTable cmp-drawer-table"
                    loading={versionsLoading}
                    columns={versionColumns}
                    dataSource={versions.map((v) => ({ ...v, key: v._id }))}
                    pagination={false}
                />
            )}

            <input type="file" ref={versionInputRef} style={{ display: "none" }} onChange={onVersionFile} />
        </Drawer>
    );
};
